import { TelemetrySink, logNotification, LogLevel, Category, TelemetryEvent, DataField } from '@ms/oteljs';
import { initialize, sendEvent, shutdown, disableBeaconsApiAvailabilityForAriaSdk } from './AriaSendEvent';
import { FullEventProcessor } from './FullEventProcessor';

/**
 * Preprocessors process a telemetry event, and return true if the event can be sent.
 * They return false if an event cannot be sent, and they throw if there is an error.
 */
export interface Preprocessor {
  processEvent: (event: TelemetryEvent) => boolean;
}

export class AriaSink implements TelemetrySink {
  private _additionalDataFields: DataField[];
  private _preprocessors: Preprocessor[] = [];
  private _fullEventProcessor: FullEventProcessor;

  /**
   * Constructs an Aria Sink
   * @param additionalDataFields Data fields to be sent with every event; these data fields are assumed to
   * be top-level static fields, and will not be prepended with "Data."
   * @param cacheMemorySizeLimitInNumberOfEvents The size limit for the cache
   */
  constructor(additionalDataFields: DataField[] = [], cacheMemorySizeLimitInNumberOfEvents?: number) {
    this._additionalDataFields = additionalDataFields;
    this._fullEventProcessor = new FullEventProcessor();
    this.addPreprocessor(this._fullEventProcessor);
    initialize({ cacheMemorySizeLimitInNumberOfEvents });
  }

  public sendTelemetryEvent(event: TelemetryEvent, timestamp?: number): void {
    try {
      for (let i = 0; i < this._preprocessors.length; i++) {
        if (!this._preprocessors[i].processEvent(event)) {
          return;
        }
      }
      sendEvent(event, this._additionalDataFields, timestamp);
    } catch (error) {
      logNotification(LogLevel.Error, Category.Sink, () => {
        let errorMessage;
        if (error instanceof Error) {
          errorMessage = error.message;
        } else {
          errorMessage = JSON.stringify(error);
        }
        return `AriaSink caught an error : ${errorMessage}`;
      });
    }
  }

  /**
   * Add a preprocessor, which can process events before they get sent
   * @param preprocessor The preprocessor to add
   */
  public addPreprocessor(preprocessor: Preprocessor) {
    this._preprocessors.push(preprocessor);
  }

  /**
   * Enable sending full events to Aria. Generally the user must explicitly allow full events to be sent.
   * @param enabled
   */
  public setFullEventsEnabled(enabled: boolean) {
    this._fullEventProcessor.setFullEventsEnabled(enabled);
  }

  /**
   * Aria SDK uses HMLT5 beacons API to send the last request to the server after it receives the shutdown call.
   * On browsers that don't support this API, it uses the traditional synchronous http POST request. The beacons
   * API is supposed to be the new hotness that allows developers to *reliably* and *asynchronously* send http requests
   * to the server during shutdown - attempting to send a generic asynchronous http request will not work reliably,
   * and sending data synchronously can be interrupted by the browser since the webpage is given a limited amount of
   * time to complete its tasks.
   * Unfortunately the API is behaving in strange ways and not sending http requests in some cases.
   * Adding this method to temporarily disable the API while we investigate the root cause.
   * VSO.2599234: Aria's shutdown beacons request is not getting honored
   * @returns - whether or not the override was successful
   */
  public disableBeaconsApiAvailabilityForAriaSdk(): boolean {
    return disableBeaconsApiAvailabilityForAriaSdk();
  }

  /**
   * Prevents new events from being added for transmission. It also batches all
   * events currently in the queue, and creates requests for them to be sent. If
   * HTML5 Beacons are supported, then they will be used.
   * In other word, make sure that this after logging events related to session end.
   */
  public shutdown() {
    shutdown();
  }
}
